
/**
 * Generates a URL-friendly slug from a given community name.
 *
 * This function performs the following transformations:
 * - Converts the input string to lowercase.
 * - Removes emoji characters and certain Unicode symbols.
 * - Removes all non-word characters except spaces and hyphens.
 * - Trims leading and trailing whitespace.
 * - Replaces consecutive spaces with a single hyphen.
 *
 * @param name - The community name to convert into a slug.
 * @returns The generated slug as a string.
 */
export const generateCommunitySlug = (name: string): string => {
  const specialCharMap: Record<string, string> = {
    'ø': 'o',
    'Ø': 'o',
    'æ': 'ae',
    'Æ': 'ae',
    'ß': 'ss',
    'ç': 'c',
    'Ç': 'c',
    'ñ': 'n',
    'Ñ': 'n',
    'ð': 'd',
    'Ð': 'd',
    'ł': 'l',
    'Ł': 'l',
  };

  // Step 1: Replace special characters manually
  const replaced = name
    .split('')
    .map(char => specialCharMap[char] || char)
    .join('');

  // Step 2: Normalize and clean
  return replaced
    .toLowerCase()
    .replace(
      /[\u{1F600}-\u{1F6FF}\u{1F300}-\u{1F5FF}\u{1F700}-\u{1F77F}\u{2600}-\u{26FF}]/gu,
      ''
    ) // remove emojis
    .replace(/_/g, '-') // underscore to hyphen
    .normalize('NFD') // split accented letters (é → e + ́)
    .replace(/[\u0300-\u036f]/g, '') // remove diacritics
    .replace(/[^a-z0-9\s-]/g, '') // remove non-word chars
    .trim()
    .replace(/\s+/g, '-') // convert spaces to hyphen
    .replace(/^-+|-+$/g, ''); // trim hyphens
};
